'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import axios from 'axios';
import { Loader2 } from 'lucide-react';
import Markdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import remarkGfm from 'remark-gfm';

import { Button } from '@kit/ui/button';

interface VideoSummaryProps {
  docId: string;
  summary: string;
  videoUrl: string;
  onDataChange: (key: string, value: string) => void;
}

export default function VideoSummary({
  docId,
  summary,
  videoUrl,
  onDataChange,
}: VideoSummaryProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleGenerateSummary = async () => {
    setIsLoading(true);
    const result = await axios.get(
      `/api/summarize/youtube?url=${videoUrl}&docId=${docId}`,
    );
    onDataChange('summary', result.data.summary);
    setIsLoading(false);
  };

  return (
    <div className="p-4">
      {summary ? (
        <Markdown
          id="video-summary"
          className="markdown textarea bg-background mb-4 min-h-[300px] w-full p-4 text-base placeholder:italic md:ml-2"
          remarkPlugins={[remarkGfm]}
          components={{
            code(props) {
              const { children, className, _, ...rest } = props;
              const match = /language-(\w+)/.exec(className || '');
              return match ? (
                <SyntaxHighlighter {...rest} PreTag="div" language={match[1]}>
                  {String(children).replace(/\n$/, '')}
                </SyntaxHighlighter>
              ) : (
                <code {...rest} className={className}>
                  {children}
                </code>
              );
            },
            a(props) {
              const { href, children, ...rest } = props;
              return (
                <a href={href} target="_blank" {...rest}>
                  {children}
                </a>
              );
            },
          }}
        >
          {summary}
        </Markdown>
      ) : (
        <div className="flex min-h-[300px] items-center justify-center text-sm">
          <Button
            variant="default"
            className="w-[180px] rounded"
            onClick={handleGenerateSummary}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="animate-spin" />
            ) : (
              'Generate Summary'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
